import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';

import { createAppTheme } from './styles/theme';
import { getCurrentUser } from './redux/slices/authSlice';
import ProtectedRoute from './common/ProtectedRoute';
import HomeRedirect from './common/HomeRedirect';
import Loading from './common/Loading';
import SuspendedDialog from './common/SuspendedDialog';
import MainLayout from './components/layout/MainLayout';

// Auth Pages
import Login from './components/pages/Login';

// Main Pages
import Dashboard from './components/pages/Dashboard';

// User Management Pages
import UsersList from './components/pages/UsersList';
import UserForm from './components/pages/UserForm';
import UserDetails from './components/pages/UserDetails';
import UserProfile from './components/pages/UserProfile';

// Role Management Pages
import RolesList from './components/pages/RolesList';
import RoleForm from './components/pages/RoleForm';
import RoleDetails from './components/pages/RoleDetails';

// Permission Management Pages
import PermissionsList from './components/pages/PermissionsList';
import PermissionForm from './components/pages/PermissionForm';
import PermissionDetails from './components/pages/PermissionDetails';
import StoresList from './components/pages/StoreList';
import StoreForm from './components/pages/StoreForm';
import StoreDetails from './components/pages/StoreDetails';
import { StoreProvider } from './context/StoreContext';
import CategoriesList from './components/pages/categories/CategoriesList';
import CategoryForm from './components/pages/categories/CategoryForm';
import CategoryDetails from './components/pages/categories/CategoryDetails';
import ProductsList from './components/pages/products/ProductsList';
import ProductForm from './components/pages/products/ProductForm';
import ProductDetails from './components/pages/products/ProductDetails';
import AttributesList from './components/pages/attributes/AttributesList';
import AttributeForm from './components/pages/attributes/AttributeForm';
import AttributeDetails from './components/pages/attributes/AttributeDetails';
import ProductVariantForm from './components/pages/product-variants/ProductVariantForm';
import StockMovementsList from './components/pages/stock-movements/StockMovementsList';
import PurchaseOrdersList from './components/pages/purchase-orders/PurchaseOrdersList';
import PurchaseOrderForm from './components/pages/purchase-orders/PurchaseOrderForm';
import PurchaseOrderDetails from './components/pages/purchase-orders/PurchaseOrderDetails';
import SuppliersList from './components/pages/suppliers/SupplierList';
import SupplierForm from './components/pages/suppliers/SupplierForm';
import SupplierDetails from './components/pages/suppliers/SupplierDetails';
import CustomersList from './components/pages/customers/CustomersList';
import CustomerForm from './components/pages/customers/CustomerForm';
import CustomerDetails from './components/pages/customers/CustomerDetails';
import PromotionsList from './components/pages/promotions/PromotionsList';
import PromotionForm from './components/pages/promotions/PromotionForm';
import PromotionDetails from './components/pages/promotions/PromotionDetails';
import OrdersList from './components/pages/orders/OrdersList';
import OrderForm from './components/pages/orders/OrderForm';
import OrderDetails from './components/pages/orders/OrderDetails';
import ShopOrdersList from './components/pages/orders/ShopOrdersList';
import NotificationsList from './components/pages/NotificationsList';
import Unauthorized from './components/pages/Unauthorized';
import WebsiteSettings from './components/pages/settings/WebsiteSettings';
import CustomOrderRequests from './components/pages/settings/CustomOrderRequests';
import SystemSettings from './components/pages/settings/SystemSettings';
import ReviewsList from './components/pages/reviews/ReviewsList';
import DeliveryList from './components/pages/delivery/DeliveryList';
import DeliveryCompaniesList from './components/pages/delivery/DeliveryCompaniesList';
import ReturnsList from './components/pages/returns/ReturnsList';
import ReturnDetails from './components/pages/returns/ReturnDetails';
import ReturnForm from './components/pages/returns/ReturnForm';

import api from './services/api';
import SetupPage from './components/setup/SetupPage';

// Emotion caches — one per direction, created once at module level
const cacheLtr = createCache({ key: 'muiltr' });
const cacheRtl = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });

function App() {
  const dispatch = useDispatch();
  const { mode } = useSelector((state) => state.theme);
  const direction = 'ltr'; // French is the only supported language (LTR)
  const { loading } = useSelector((state) => state.auth);
  const [initializing, setInitializing] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  // null = active; otherwise the reason shown by the blocking SuspendedDialog.
  const [suspension, setSuspension] = useState(null);

  const theme = React.useMemo(
    () => createAppTheme(mode, direction),
    [mode, direction]
  );

  React.useEffect(() => {
    // First check if setup is needed
    // Suspended mid-session: any blocked API call (503 suspended) raises this.
    const onSuspended = (e) => setSuspension({ reason: e.detail?.reason || '' });
    window.addEventListener('invotrix:suspended', onSuspended);

    api.get('/setup/status')
      .then(res => {
        if (res.data.suspended) {
          setSuspension({ reason: res.data.suspensionReason || '' });
          setInitializing(false);
        } else if (res.data.setupRequired) {
          setSetupRequired(true);
          setInitializing(false);
        } else {
          // Setup done — load current user
          dispatch(getCurrentUser()).finally(() => setInitializing(false));
        }
      })
      .catch(() => {
        // If setup check fails assume normal flow
        dispatch(getCurrentUser()).finally(() => setInitializing(false));
      });

    return () => window.removeEventListener('invotrix:suspended', onSuspended);
  }, [dispatch]);

  // Suspended: nothing but the blocking popup — no routes, no layout.
  if (suspension) {
    return (
      <CacheProvider value={direction === 'rtl' ? cacheRtl : cacheLtr}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SuspendedDialog reason={suspension.reason} />
        </ThemeProvider>
      </CacheProvider>
    );
  }

  if (initializing || loading) {
    return <Loading />;
  }

  return (
    <CacheProvider value={direction === 'rtl' ? cacheRtl : cacheLtr}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Routes>

            {/* Setup Route */}
            <Route
                path="/setup"
                element={
                    setupRequired
                        ? <SetupPage onComplete={() => setSetupRequired(false)} />
                        : <HomeRedirect />
                }
            />

            {/* Public Routes */}
            <Route
              path="/login"
              element={setupRequired ? <Navigate to="/setup" replace /> : <Login />}
            />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                setupRequired
                  ? <Navigate to="/setup" replace />
                  : <ProtectedRoute>
                      <StoreProvider>
                        <MainLayout />
                      </StoreProvider>
                    </ProtectedRoute>
              }
            >
              <Route index element={<HomeRedirect />} />
              <Route path="dashboard" element={<ProtectedRoute requiredPermission="dashboard.read"><Dashboard /></ProtectedRoute>} />
              <Route path="profile" element={<UserProfile />} />
              <Route path="notifications" element={<NotificationsList />} />
              <Route path="unauthorized" element={<Unauthorized />} />
              <Route path="settings/website" element={<WebsiteSettings />} />
              <Route path="settings/custom-orders" element={<CustomOrderRequests />} />
              <Route path="reviews" element={<ReviewsList />} />
              <Route path="settings/system" element={<SystemSettings />} />
              <Route path="delivery" element={<ProtectedRoute requiredPermission="stores.read"><DeliveryList /></ProtectedRoute>} />
              <Route path="delivery-companies" element={<ProtectedRoute requiredPermission="stores.read"><DeliveryCompaniesList /></ProtectedRoute>} />

              <Route path="users" element={<ProtectedRoute requiredPermission="users.read"><UsersList /></ProtectedRoute>} />
              <Route path="users/create" element={<ProtectedRoute requiredPermission="users.create"><UserForm /></ProtectedRoute>} />
              <Route path="users/edit/:id" element={<ProtectedRoute requiredPermission="users.update"><UserForm /></ProtectedRoute>} />
              <Route path="users/view/:id" element={<ProtectedRoute requiredPermission="users.read"><UserDetails /></ProtectedRoute>} />

              <Route path="roles" element={<ProtectedRoute requiredPermission="roles.read"><RolesList /></ProtectedRoute>} />
              <Route path="roles/create" element={<ProtectedRoute requiredPermission="roles.create"><RoleForm /></ProtectedRoute>} />
              <Route path="roles/edit/:id" element={<ProtectedRoute requiredPermission="roles.update"><RoleForm /></ProtectedRoute>} />
              <Route path="roles/view/:id" element={<ProtectedRoute requiredPermission="roles.read"><RoleDetails /></ProtectedRoute>} />

              <Route path="permissions" element={<ProtectedRoute requiredPermission="permissions.read"><PermissionsList /></ProtectedRoute>} />
              <Route path="permissions/create" element={<ProtectedRoute requiredPermission="permissions.create"><PermissionForm /></ProtectedRoute>} />
              <Route path="permissions/edit/:id" element={<ProtectedRoute requiredPermission="permissions.update"><PermissionForm /></ProtectedRoute>} />
              <Route path="permissions/view/:id" element={<ProtectedRoute requiredPermission="permissions.read"><PermissionDetails /></ProtectedRoute>} />

              <Route path="stores" element={<ProtectedRoute requiredPermission="stores.read"><StoresList /></ProtectedRoute>} />
              <Route path="stores/create" element={<ProtectedRoute requiredPermission="stores.create"><StoreForm /></ProtectedRoute>} />
              <Route path="stores/edit/:id" element={<ProtectedRoute requiredPermission="stores.update"><StoreForm /></ProtectedRoute>} />
              <Route path="stores/view/:id" element={<ProtectedRoute requiredPermission="stores.read"><StoreDetails /></ProtectedRoute>} />

              <Route path="categories" element={<ProtectedRoute requiredPermission="categories.read"><CategoriesList /></ProtectedRoute>} />
              <Route path="categories/create" element={<ProtectedRoute requiredPermission="categories.create"><CategoryForm /></ProtectedRoute>} />
              <Route path="categories/edit/:id" element={<ProtectedRoute requiredPermission="categories.update"><CategoryForm /></ProtectedRoute>} />
              <Route path="categories/view/:id" element={<ProtectedRoute requiredPermission="categories.read"><CategoryDetails /></ProtectedRoute>} />

              <Route path="products">
                <Route index element={<ProtectedRoute requiredPermission="products.read"><ProductsList /></ProtectedRoute>} />
                <Route path="create" element={<ProtectedRoute requiredPermission="products.create"><ProductForm /></ProtectedRoute>} />
                <Route path="edit/:id" element={<ProtectedRoute requiredPermission="products.update"><ProductForm /></ProtectedRoute>} />
                <Route path="view/:id" element={<ProtectedRoute requiredPermission="products.read"><ProductDetails /></ProtectedRoute>} />
                <Route path=":productId/variants/new" element={<ProtectedRoute requiredPermission="products.update"><ProductVariantForm /></ProtectedRoute>} />
                <Route path=":productId/variants/edit/:id" element={<ProtectedRoute requiredPermission="products.update"><ProductVariantForm /></ProtectedRoute>} />
              </Route>

              <Route path="attributes" element={<ProtectedRoute requiredPermission="attributes.read"><AttributesList /></ProtectedRoute>} />
              <Route path="attributes/create" element={<ProtectedRoute requiredPermission="attributes.create"><AttributeForm /></ProtectedRoute>} />
              <Route path="attributes/edit/:id" element={<ProtectedRoute requiredPermission="attributes.update"><AttributeForm /></ProtectedRoute>} />
              <Route path="attributes/view/:id" element={<ProtectedRoute requiredPermission="attributes.read"><AttributeDetails /></ProtectedRoute>} />

              <Route path="stock-movements" element={<ProtectedRoute requiredPermission="stock.read"><StockMovementsList /></ProtectedRoute>} />

              {/* Real seeded permission name is "purchase_orders.*" (underscore) — see DatabaseInitializer#createPermissions */}
              <Route path="purchase-orders" element={<ProtectedRoute requiredPermission="purchase_orders.read"><PurchaseOrdersList /></ProtectedRoute>} />
              <Route path="purchase-orders/create" element={<ProtectedRoute requiredPermission="purchase_orders.create"><PurchaseOrderForm /></ProtectedRoute>} />
              <Route path="purchase-orders/edit/:id" element={<ProtectedRoute requiredPermission="purchase_orders.update"><PurchaseOrderForm /></ProtectedRoute>} />
              <Route path="purchase-orders/:id" element={<ProtectedRoute requiredPermission="purchase_orders.read"><PurchaseOrderDetails /></ProtectedRoute>} />

              <Route path="suppliers" element={<ProtectedRoute requiredPermission="suppliers.read"><SuppliersList /></ProtectedRoute>} />
              <Route path="suppliers/create" element={<ProtectedRoute requiredPermission="suppliers.create"><SupplierForm /></ProtectedRoute>} />
              <Route path="suppliers/edit/:id" element={<ProtectedRoute requiredPermission="suppliers.update"><SupplierForm /></ProtectedRoute>} />
              <Route path="suppliers/:id" element={<ProtectedRoute requiredPermission="suppliers.read"><SupplierDetails /></ProtectedRoute>} />

              <Route path="customers" element={<ProtectedRoute requiredPermission="customers.read"><CustomersList /></ProtectedRoute>} />
              <Route path="customers/create" element={<ProtectedRoute requiredPermission="customers.create"><CustomerForm /></ProtectedRoute>} />
              <Route path="customers/edit/:id" element={<ProtectedRoute requiredPermission="customers.update"><CustomerForm /></ProtectedRoute>} />
              <Route path="customers/:id" element={<ProtectedRoute requiredPermission="customers.read"><CustomerDetails /></ProtectedRoute>} />

              <Route path="promotions" element={<ProtectedRoute requiredPermission="promotions.read"><PromotionsList /></ProtectedRoute>} />
              <Route path="promotions/create" element={<ProtectedRoute requiredPermission="promotions.create"><PromotionForm /></ProtectedRoute>} />
              <Route path="promotions/edit/:id" element={<ProtectedRoute requiredPermission="promotions.update"><PromotionForm /></ProtectedRoute>} />
              <Route path="promotions/view/:id" element={<ProtectedRoute requiredPermission="promotions.read"><PromotionDetails /></ProtectedRoute>} />

              <Route path="orders" element={<ProtectedRoute requiredPermission="orders.read"><OrdersList /></ProtectedRoute>} />
              <Route path="orders/create" element={<ProtectedRoute requiredPermission="orders.create"><OrderForm /></ProtectedRoute>} />
              <Route path="orders/:id/edit" element={<ProtectedRoute requiredPermission="orders.update"><OrderForm /></ProtectedRoute>} />
              <Route path="orders/:id" element={<ProtectedRoute requiredPermission="orders.read"><OrderDetails /></ProtectedRoute>} />

              <Route path="shop-orders" element={<ProtectedRoute requiredPermission="shop_orders.read"><ShopOrdersList /></ProtectedRoute>} />

              <Route path="returns" element={<ProtectedRoute requiredPermission="orders.read"><ReturnsList /></ProtectedRoute>} />
              <Route path="returns/create/:orderId" element={<ProtectedRoute requiredPermission="orders.update"><ReturnForm /></ProtectedRoute>} />
              <Route path="returns/:id" element={<ProtectedRoute requiredPermission="orders.read"><ReturnDetails /></ProtectedRoute>} />
            </Route>

            {/* Fallback */}
            <Route
              path="*"
              element={
                setupRequired
                  ? <Navigate to="/setup" replace />
                  : <Navigate to="/" replace />
              }
            />

          </Routes>
        </BrowserRouter>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={mode === 'light' ? 'light' : 'dark'}
        />
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;